import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowRight, MessageSquareText } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { resources } from "@/data/resources";
import { SITE_URL } from "@/lib/prerender-routes";

const TITLE = "Free Resources for Home-Service Businesses | The Alpha Omega Hub";
const DESC = "Free playbooks, guides and AI tools we use to book appointments for remodelers and home-service businesses. Get them free.";

const Resources = () => {
  const itemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Free resources for home-service businesses",
    itemListElement: resources.map((r, i) => ({
      "@type": "ListItem", position: i + 1, name: r.title, url: `${SITE_URL}${r.href}`,
    })),
  };
  return (
    <main className="min-h-screen bg-background overflow-x-hidden">
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESC} />
        <link rel="canonical" href={`${SITE_URL}/resources`} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESC} />
        <meta property="og:url" content={`${SITE_URL}/resources`} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(itemList)}</script>
      </Helmet>
      <Navbar />

      <section className="px-5 pb-10 pt-32 text-center md:pt-40">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-accent md:text-sm">FREE RESOURCES</p>
        <h1 className="mx-auto mt-4 max-w-3xl font-display text-4xl font-bold leading-tight text-foreground md:text-6xl">
          Free resources for home-service businesses
        </h1>
        <p className="mx-auto mt-5 max-w-2xl text-base text-muted-foreground md:text-lg">
          Playbooks, guides and AI tools we use to book appointments for our clients. Free.
        </p>
      </section>

      <section className="px-5 pb-24">
        <ul className="mx-auto grid max-w-6xl gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {resources.map((r) => (
            <li key={r.href} className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card/60 backdrop-blur">
              <Link to={r.href} className="block aspect-[16/10] overflow-hidden bg-secondary" tabIndex={-1} aria-hidden>
                {r.image ? (
                  <img src={r.image} alt={r.imageAlt ?? r.title} loading="lazy" className="h-full w-full object-cover object-top" />
                ) : (
                  <span className="flex h-full w-full items-center justify-center bg-primary">
                    <MessageSquareText className="h-16 w-16 text-accent" aria-hidden />
                  </span>
                )}
              </Link>
              <div className="flex flex-1 flex-col p-6">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-accent">{r.type}</p>
                <h2 className="mt-2 font-display text-xl font-bold leading-snug text-foreground">{r.title}</h2>
                <p className="mt-3 text-sm text-muted-foreground">{r.description}</p>
                <Link to={r.href}
                  className="mt-6 inline-flex items-center justify-center gap-2 self-start rounded-lg bg-accent px-5 py-3 font-bold text-accent-foreground transition hover:brightness-105">
                  Get it free <ArrowRight className="h-4 w-4" aria-hidden />
                </Link>
              </div>
            </li>
          ))}
        </ul>
      </section>
      <Footer />
    </main>
  );
};

export default Resources;
